import {SpaceTr, Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import {Answer, Replica} from "@/store/meet/types";
import TextButton from "@/scenes.ui/text-button";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {Creature} from "@/store/level/types";
import {SkillType} from "@/store/place/types";
import TextUtils from "@/utils/text-utils";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import {Fonts} from "@/constants/font";
import {SquareButton} from "@/scenes.ui/square-button";
import Text = Phaser.GameObjects.Text;
import Image = Phaser.GameObjects.Image;
import GameObject = Phaser.GameObjects.GameObject;
import {CustomPlaceContainer} from "@/scenes.ui/custom-place-container";
import PlayerStore from "@/store/player";
import SpriteUtils from "@/utils/sprite-utils";
import SkillUtils from "@/utils/skill-utils";
import BBCodeText from "phaser3-rex-plugins/plugins/bbcodetext";
import CU from "@/utils/c-u";
import AtlasUtils from "@/utils/atlas-utils";

enum Mode { SINGLE_BUTTON, TEXT_CHOICE, PICTURE_CHOICE, CREATURE_CHOICE, SQ_BUTTON, SQ_BUTTON_CHOICE }

export class CustomPlacePanel extends VerticalStandardPanel implements CustomPlaceContainer {

    private readonly Z_INDEX: number = 100;

    private callback: ((answer: Answer) => unknown) | undefined = undefined;

    private replica: Replica | undefined;

    private mainText: Text | undefined;
    private agitationText: Text | undefined;
    private bbCodeText: BBCodeText | undefined;
    private image: Image | undefined;
    private imageComment: Text | undefined;

    private awardImage: Image | undefined;
    private awardImage2: Image | undefined;
    private awardImageComment: Text | undefined;
    private awardImageComment2: Text | undefined;

    private singleButton: TextButton | SquareButton | undefined;

    private leftButton: TextButton | SquareButton | undefined;
    private rightButton: TextButton | SquareButton | undefined;
    private thirdButton: TextButton | SquareButton | undefined;

    private leftFrameButton: SquareButton | undefined;
    private rightFrameButton: SquareButton | undefined;
    private thirdFrameButton: SquareButton | undefined;

    private leftCreaturePanel: CreaturePanel | undefined;
    private rightCreaturePanel: CreaturePanel | undefined;
    private thirdCreaturePanel: CreaturePanel | undefined;

    private mode: Mode = Mode.SINGLE_BUTTON;

    private exitCallback!: (removePlace: boolean, setCompleted: boolean) => unknown;

    constructor(scene: Scene, canvas: HTMLCanvasElement, exitCallback: (removePlace: boolean, setCompleted: boolean) => unknown) {
        super(scene, canvas, '');

        this.setDepth(this.Z_INDEX);
        this.exitCallback = exitCallback;
    }

    public init(replica: Replica, callback: (answer: Answer) => unknown): void {
        this.replica = replica;
        this.mode = this.defineMode();
        this.callback = callback;
        this.setTitle(replica.title != null ? replica.title : '');

        this.setup(replica);
    }

    private defineMode(): Mode {
        if (this.replica!.answers && this.replica!.answers.length == 1) {
            if (this.replica!.answers[0].buttonId) {
                return Mode.SQ_BUTTON;
            }
            return Mode.SINGLE_BUTTON;
        } else if (this.replica!.answers && this.replica!.answers.length == 2) {
            if (this.replica!.answers[0].buttonId) {
                return Mode.SQ_BUTTON_CHOICE;
            }
            if (this.replica!.answers[0].creatureId && this.replica!.answers[1].creatureId) {
                return Mode.CREATURE_CHOICE;
            } else if (this.replica!.answers[0].imageId && this.replica!.answers[1].imageId) {
                return Mode.PICTURE_CHOICE;
            } else {
                return Mode.TEXT_CHOICE;
            }
        }
        console.log('Can not handle such a setting of Custom Place');
        throw new Error();
    }

    private initAwardBlock(replica: Replica): void {
        let imageKey = CustomPlacePanel.getAwardImage(replica);
        let imageAtlas = CustomPlacePanel.getImageAtlas(replica);
        let imageComment = CustomPlacePanel.getAwardComment(replica);

        let dx= 80;

        if (imageKey) {
            let parts = imageKey.split('|');
            this.awardImage = SpriteUtils.addImage(this.scene, 0 + (parts.length > 1? -dx: 0), 60, imageAtlas? imageAtlas : parts[0], imageAtlas? parts[0] : undefined);
            this.add(this.awardImage);

            if(parts.length > 1){
                this.awardImage2 = SpriteUtils.addImage(this.scene, dx, 60, imageAtlas? imageAtlas : parts[1], imageAtlas? parts[1] : undefined);
                this.add(this.awardImage2);
            }
        }

        if (imageComment) {
            let parts = imageComment.split('|');
            this.awardImageComment = TextUtils.addTextByFamily(this.scene, parts[0].toUpperCase(),
                + (parts.length > 1? -dx: 0), 115, '#bfbfbf', 13, 'normal', 450, Fonts.MYRIADPRO_BOLD);
            this.add(this.awardImageComment);

            if(parts.length > 1){
                this.awardImageComment2 = TextUtils.addTextByFamily(this.scene, parts[1].toUpperCase(),
                    dx, 115, '#bfbfbf', 13, 'normal', 450, Fonts.MYRIADPRO_BOLD);
                this.add(this.awardImageComment2);
            }
        }
    }

    private initAnswerBlock(replica: Replica): void {
        this.replica = replica;
        const self = this;
        this.mode = this.defineMode();

        switch (this.mode) {
            case Mode.SINGLE_BUTTON:
            case Mode.SQ_BUTTON:
                let a: Answer = replica.answers![0];
                this.singleButton = this.mode == Mode.SINGLE_BUTTON ? new TextButton(this.scene, 0, 190, Keys.UI_BUTTON_BROWN, a.text!)
                    : new SquareButton(this.scene, 0, 160, 0, a.buttonId!, a.atlasId, a.imageId, a.text!, false);
                this.add(this.singleButton);
                this.singleButton!.setCallback(() => {
                    if (self.replica && self.replica.answers) {
                        this.callback!(self.replica.answers[0]);
                    }
                });
                break;
            case Mode.TEXT_CHOICE:
            case Mode.SQ_BUTTON_CHOICE:

                let ans1 = replica.answers![0];
                let ans2 = replica.answers![1];
                let ans3;

                if (replica.answers![2]) ans3 = replica.answers![2];
                
                this.leftButton = this.mode == Mode.TEXT_CHOICE ? new TextButton(this.scene, -90, 180, Keys.UI_BUTTON_BROWN_SMALL, replica.answers![0].text!)
                    : new SquareButton(this.scene, -100, 160, 0, ans1.buttonId!, ans1.atlasId, ans1.imageId, ans1.text!, false);

                this.rightButton = this.mode == Mode.TEXT_CHOICE ? new TextButton(this.scene, 90, 180, Keys.UI_BUTTON_BROWN_SMALL, replica.answers![1].text!)
                    : new SquareButton(this.scene, 100, 160, 0, ans2.buttonId!, ans2.atlasId, ans2.imageId, ans2.text!, false);

                // this.leftButton = new SquareButton(this.scene, -100, 180, -10,
                //     Keys.UI_ART_BROWN_BUTTON, Keys.UI_GOLDEN_CHECK, replica.answers![0].text!, false, 64, 64, 45);
                // this.rightButton = new SquareButton(this.scene, 100, 180, -10,
                //     Keys.UI_ART_BROWN_BUTTON, Keys.UI_GOLDEN_CHECK, replica.answers![1].text!, false, 64, 64, 45);
                this.add(this.leftButton);
                this.add(this.rightButton);

                this.leftButton!.setCallback(() => {
                    this.callback!(self.replica!.answers![0]);
                });
                this.rightButton!.setCallback(() => {
                    this.callback!(self.replica!.answers![1]);
                });
                
                if (ans3) {
                    this.leftButton.setScale(0.6, 0.6).setX(this.leftButton.x - 60);
                    this.rightButton.setScale(0.6, 0.6).setX(this.rightButton.x - 115);

                    this.thirdButton = this.mode == Mode.TEXT_CHOICE ? new TextButton(this.scene, 138, 180, Keys.UI_BUTTON_BROWN_SMALL, replica.answers![2].text!)
                    : new SquareButton(this.scene, 100, 160, 0, ans3.buttonId!, ans3.atlasId, ans3.imageId, ans3.text!, false);

                    this.thirdButton.setScale(0.6, 0.6);

                    this.add(this.thirdButton);

                    this.thirdButton!.setCallback(() => {
                        this.callback!(self.replica!.answers![2]);
                    });
                }
                break;
            case Mode.PICTURE_CHOICE:
                let left: Answer = replica.answers![0];
                let right: Answer = replica.answers![1];
                let third: Answer|undefined;

                if (this.thirdButton) third = replica.answers![2];

                // this.leftFrameButton = new FrameButton(this.scene, -100, this.getAnswersPicturesY(), Keys.UI_SQUARE_FRAME, left.imageId!, left.text!);
                // this.rightFrameButton = new FrameButton(this.scene, 100, this.getAnswersPicturesY(), Keys.UI_SQUARE_FRAME, right.imageId!, right.text!);
                this.leftFrameButton = new SquareButton(this.scene, -100, this.getAnswersPicturesY(), 0,
                    Keys.UI_SQUARE_FRAME, left.atlasId, left.imageId!, left.text!, false);
                this.rightFrameButton = new SquareButton(this.scene, 100, this.getAnswersPicturesY(), 0,
                    Keys.UI_SQUARE_FRAME, right.atlasId, right.imageId!, right.text!, false);
                
                this.add(this.leftFrameButton);
                this.add(this.rightFrameButton);

                this.leftFrameButton!.setCallback(() => {
                    this.callback!(self.replica!.answers![0]);
                });
                this.rightFrameButton!.setCallback(() => {
                    this.callback!(self.replica!.answers![1]);
                });

                if (third) {
                    this.thirdFrameButton = new SquareButton(this.scene, 0, this.getAnswersPicturesY() + 70, 0,
                    Keys.UI_SQUARE_FRAME, third.atlasId, third.imageId!, third.text!, false);
                    
                    this.add(this.thirdFrameButton);

                    this.thirdFrameButton!.setCallback(() => {
                        this.callback!(self.replica!.answers![2]);
                    });
                }
                break;
            case Mode.CREATURE_CHOICE:
                let creatures: Creature[] = PlayerStore.getPlayer()!.creatures.filter((c:Creature) => !c.temporal);
                let leftCreature = creatures.filter((c: Creature) => c.id === replica.answers![0].crId)[0];
                let rightCreature = creatures.filter((c: Creature) => c.id === replica.answers![1].crId)[0];
                let thirdCreature;

                if (this.thirdButton) thirdCreature = creatures.filter((c: Creature) => c.id === replica.answers![2].crId)[0];
                
                self.leftCreaturePanel = new CreaturePanel(this.scene, -90, this.getAnswersPicturesY(), leftCreature, () => {});
                self.rightCreaturePanel = new CreaturePanel(this.scene, 90, this.getAnswersPicturesY(), rightCreature, () => {});
                
                self.leftCreaturePanel.setHealthVisible();
                self.rightCreaturePanel.setHealthVisible();
                
                self.leftCreaturePanel.setNameText(leftCreature.name);
                self.rightCreaturePanel.setNameText(rightCreature.name);
                
                self.add(self.leftCreaturePanel);
                self.add(self.rightCreaturePanel);
                
                self.leftCreaturePanel!.setCallback(() => {
                    self.callback!(self.replica!.answers![0]);
                });
                self.rightCreaturePanel!.setCallback(() => {
                    self.callback!(self.replica!.answers![1]!);
                });

                if (thirdCreature) {
                    self.thirdCreaturePanel = new CreaturePanel(this.scene, 0, this.getAnswersPicturesY() + 100, rightCreature, () => {});
                    
                    self.thirdCreaturePanel.setHealthVisible();
                    self.thirdCreaturePanel.setNameText(thirdCreature.name);

                    self.add(self.thirdCreaturePanel);

                    self.thirdCreaturePanel!.setCallback(() => {
                        self.callback!(self.replica!.answers![2]);
                    });
                }

                break;
        }
    }

    public setup(replica: Replica): void {
        if (replica.bbCode) {
            let w = 420
            if (this.bbCodeText) {
                this.bbCodeText!.setText(replica.mainText!);
            } else {
                this.bbCodeText = new BBCodeText(this.scene, -w * CU.S / 2, this.getXButtonY() * CU.S + 210 * CU.S, replica.mainText, {
                    fontFamily: Fonts.ARIAL_MT,
                    fontSize: '14pt',
                    align: 'center',
                    fixedWidth: w,
                    wrap: {
                        mode: 1,
                        width: w * CU.S
                    },
                    shadow: {
                        offsetX: -1,
                        offsetY: 2,
                        color: '#000000'
                    },
                    lineSpacing: 3,
                    stroke: '#000000',
                }).setScale(CU.S, CU.S);
                this.add(this.bbCodeText);
            }
        } else {
            if (this.mainText) {
                this.mainText!.setText(replica.mainText!);
            } else {
                this.mainText = TextUtils.addTextByFamily(this.scene, replica.mainText!,
                    0, this.getMainTextY(), '#bfbfbf', this.getMainTextSize(), 'normal', 420, Fonts.ARIAL_MT);
                this.add(this.mainText);
            }
        }

        if (replica.agitationText) {
            if (this.agitationText) {
                this.agitationText!.setText(replica.agitationText);
            } else {
                this.agitationText = TextUtils.addTextByFamily(this.scene, replica.agitationText.toUpperCase(),
                    0, this.getAgitationTextY(), '#e7e7e7', 13, 'normal', 450, Fonts.ARIAL_MT);
                this.add(this.agitationText);
            }
        } else if (this.agitationText) {
            this.agitationText.destroy();
            this.agitationText = undefined;
        }

        if (this.image) {
            this.image.destroy();
            this.image = undefined;
        }

        let isBigPicture = replica.imageId && replica.imageId.startsWith("pic_");

        if (replica.imageId) {
            if (replica.atlasId === 'atlas_places') {
                replica.atlasId = 'atlas_places_1'; // hack for backward compatibility, don't remove it.
            }
            let dy =  isBigPicture? 52 : 0;
            let atlasId = AtlasUtils.getReplicaAtlas(replica);
            let imageId = AtlasUtils.getReplicaImage(replica);
            this.image = !!atlasId ? SpriteUtils.addImage(this.scene, 0, -170 + dy, atlasId, imageId) : SpriteUtils.addImage(this.scene, 0, -180 + dy, imageId);
            if (this.image.height > 100 && !isBigPicture) {
                SpriteUtils.scale(this.image, 100 / this.image.height);
                this.image.y += 10;
            }
            this.add(this.image);
        }
        if (replica.imageComment && !isBigPicture) {
            if (this.imageComment) {
                this.imageComment!.setText(replica.imageComment);
            } else {
                this.imageComment = TextUtils.addTextByFamily(this.scene, replica.imageComment.toUpperCase(),
                    0, -110, '#ffd465', 18, 'normal', 450, Fonts.MYRIADPRO_BOLD);
                this.add(this.imageComment);
            }
        } else if (this.imageComment) {
            this.imageComment.destroy();
            this.imageComment = undefined;
        }

        CustomPlacePanel.destroyIfPresent(this.singleButton);
        CustomPlacePanel.destroyIfPresent(this.leftButton);
        CustomPlacePanel.destroyIfPresent(this.rightButton);
        CustomPlacePanel.destroyIfPresent(this.leftFrameButton);
        CustomPlacePanel.destroyIfPresent(this.rightFrameButton);
        CustomPlacePanel.destroyIfPresent(this.leftCreaturePanel);
        CustomPlacePanel.destroyIfPresent(this.rightCreaturePanel);

        if (this.thirdButton) {
            CustomPlacePanel.destroyIfPresent(this.thirdButton);
            CustomPlacePanel.destroyIfPresent(this.thirdFrameButton);
            CustomPlacePanel.destroyIfPresent(this.thirdCreaturePanel);
        }

        CustomPlacePanel.destroyIfPresent(this.awardImage);
        CustomPlacePanel.destroyIfPresent(this.awardImage2);
        CustomPlacePanel.destroyIfPresent(this.awardImageComment);
        CustomPlacePanel.destroyIfPresent(this.awardImageComment2);

        this.initAnswerBlock(replica);
        this.initAwardBlock(replica);

        this.xButton!.setVisible(replica.closable!);
    }

    private static destroyIfPresent(obj: GameObject | undefined): void {
        if (obj) {
            obj.destroy();
        }
    }

    private getMainTextY(): number {
        let res: number = 0;
        switch (this.mode) {
            case Mode.SINGLE_BUTTON:
                res = -20;
                break;
            case Mode.TEXT_CHOICE:
                res = -20;
                break;
            case Mode.PICTURE_CHOICE:
                res = -25;
                break;
            case Mode.CREATURE_CHOICE:
                res = -30;
                break;
        }

        if (this.replica!.agitationText) {
            res -= 20;
        }

        if (this.replica!.award) {
            res -= 20;
        }

        return res;
    }

    private getAgitationTextY(): number {
        let res: number = 0;
        switch (this.mode) {
            case Mode.SINGLE_BUTTON:
                res = 35;
                break;
            case Mode.TEXT_CHOICE:
                res = 35;
                break;
            case Mode.PICTURE_CHOICE:
                res = 35;
                break;
            case Mode.CREATURE_CHOICE:
                res = 30;
                break;
        }

        return res;
    }

    private getMainTextSize(): number {
        if (this.replica!.agitationText || this.replica!.award) {
            return 13;
        }
        return 15;
    }

    private getAnswersPicturesY(): number {
        if (this.replica!.agitationText) {
            return 150;
        }
        return 135;
    }

    private static getImageAtlas(replica: Replica): string | undefined {
        if (replica.award){
            if (replica.award.goodPoints && replica.award.goodPoints > 0) {
                return undefined;
            }

            if (replica.award.evilPoints && replica.award.evilPoints > 0) {
                return undefined;
            }

            if (replica.award.tickets && replica.award.tickets > 0) {
                return undefined;
            }

            if (replica.award.amount && replica.award.amount.caviar && replica.award.amount.caviar > 0) {
                return undefined;
            }

            if (replica.award.atlasId){
                if(replica.award.atlasId.toLowerCase() == "no_atlas"){
                    return undefined;
                }

                return replica.award.atlasId;
            }
        }
        return Keys.ATLAS_IC_100;
    }
    private static getAwardImage(replica: Replica): string | undefined {
        if (replica.award) {
            let amount = replica.award.amount;
            if (amount) {
                if (amount.crystal > 0) {
                    return Keys.IC_100_CRYSTAL;
                } else if (amount.gold > 0) {
                    return Keys.IC_100_GOLD;
                } else if (amount.elixir > 0) {
                    return Keys.IC_100_ELIXIR;
                } else if (amount.mithril > 0) {
                    return Keys.IC_100_MITHRIL;
                } else if (amount.sulfur > 0) {
                    return Keys.IC_100_SULFUR;
                } else if (amount.caviar > 0) {
                    return Keys.IC_100_CAVIAR;
                }
            }

            let skillType = replica.award.skillType;

            if (skillType) {
                switch (skillType) {
                    case SkillType.ATTACK:
                        return Keys.IC_100_ATTACK;
                    case SkillType.DEFENCE:
                        return Keys.IC_100_DEFENCE;
                    case SkillType.HEALTH:
                        return Keys.IC_100_HEALTH;
                }
            }

            if (replica.award.experience && replica.award.experience > 0) {
                return Keys.IC_100_EXP;
            }

            if(replica.award.goodPoints && replica.award.goodPoints > 0 && replica.award.evilPoints && replica.award.evilPoints > 0){
                return Keys.UI_PROFILE_EVIL_SIGN_BIG + '|' + Keys.UI_PROFILE_GOOD_SIGN_BIG;
            }

            if (replica.award.goodPoints && replica.award.goodPoints > 0) {
                return Keys.UI_PROFILE_GOOD_SIGN_BIG;
            }

            if (replica.award.evilPoints && replica.award.evilPoints > 0) {
                return Keys.UI_PROFILE_EVIL_SIGN_BIG;
            }

            if (replica.award.tickets && replica.award.tickets > 0) {
                return Keys.IMG_TICKET_BIG;
            }

            if (replica.award.imageId){
                return replica.award.imageId;
            }

        }
        return undefined;
    }

    private static getAwardComment(replica: Replica): string | undefined {
        if (replica.award) {
            let amount = replica.award.amount;
            if (amount) {
                if (amount.crystal > 0) {
                    return `${TextUtils.formatMoney(amount.crystal)}` +
                        TextUtils.chooseWord(amount.crystal, SpaceTr('krasniy.kristall'), SpaceTr('red.cr.gen'), SpaceTr('red.cr.mul.gen'));
                } else if (amount.gold > 0) {
                    return `${TextUtils.formatMoney(amount.gold)} ${Tr('gold.gen')}`;
                } else if (amount.elixir > 0) {
                    return `${TextUtils.formatMoney(amount.elixir)}` +
                        TextUtils.chooseWord(amount.crystal, SpaceTr('siniy.kristall'), SpaceTr('blue.cr.gen'), SpaceTr('blue.cr.mul.gen'));
                } else if (amount.mithril > 0) {
                    return String(amount.mithril) + ' ' + Tr('gems.mul.gen');
                } else if (amount.sulfur > 0) {
                    return `${amount.sulfur} ${Tr('sulphur')}`;
                } else if (amount.caviar > 0) {
                    return `${amount.caviar} ${Tr('cav.g')}`;
                }
            }

            let skillType = replica.award.skillType;

            if (skillType) {
                switch (skillType) {
                    case SkillType.ATTACK:
                        return `${Tr('ataka')} +${SkillUtils.formatValue(replica.award.skillDelta)}`;
                    case SkillType.DEFENCE:
                        return `${Tr('zaschita')} +${SkillUtils.formatValue(replica.award.skillDelta)}`;
                    case SkillType.HEALTH:
                        return `${Tr('zdorove')} +${SkillUtils.formatValue(replica.award.skillDelta)}`;
                }
            }

            if (replica.award.experience) {
                return `${Tr('opit')} +${replica.award.experience}`;
            }

            if(replica.award.goodPoints && replica.award.evilPoints){
                return `${Tr('evil.points')} +${replica.award.evilPoints}` + '|' + `${Tr('good.points')} +${replica.award.goodPoints}`;
            }

            if (replica.award.goodPoints) {
                return `${Tr('good.points')} +${replica.award.goodPoints}`;
            }

            if (replica.award.evilPoints) {
                return `${Tr('evil.points')} +${replica.award.evilPoints}`;
            }

            if (replica.award.tickets) {
                return `${Tr('tickets')} +${replica.award.tickets}`;
            }

            if (replica.award.comment){
                return replica.award.comment;
            }
        }
    }

    protected preClose(): void {
        CustomPlacePanel.destroyIfPresent(this.singleButton);
        CustomPlacePanel.destroyIfPresent(this.leftButton);
        CustomPlacePanel.destroyIfPresent(this.rightButton);
        
        CustomPlacePanel.destroyIfPresent(this.leftFrameButton);
        CustomPlacePanel.destroyIfPresent(this.rightFrameButton);
        
        CustomPlacePanel.destroyIfPresent(this.leftCreaturePanel);
        CustomPlacePanel.destroyIfPresent(this.rightCreaturePanel);


        this.thirdButton && CustomPlacePanel.destroyIfPresent(this.thirdButton);
        this.thirdFrameButton && CustomPlacePanel.destroyIfPresent(this.thirdFrameButton);
        this.thirdCreaturePanel && CustomPlacePanel.destroyIfPresent(this.thirdCreaturePanel);
        super.preClose();
    }

    protected onClose(): void {
        this.exitCallback!(false, false);
    }

    public openAnimated(): void {
        this.doOpenAnimated(40, 300);
    }

    public isSceneDefined() {
        return !!this.scene;
    }
}
